// ============================================================================
// FSA ADJUSTMENT JOURNAL SERVICE  (FSA-2.2 Adjustments)
// ============================================================================

import { get, post, put, del } from '../../client/axiosClient';
import { fsaAdjustmentJournalEndpoints } from '../../endpoints/fsa/fsaAdjustmentJournal.endpoints';
import type { ApiResponse, ID, PaginatedResponse } from '../../types';
import type {
  AdjustmentJournal,
  AdjustmentJournalQueryParameters,
  AuditedLedgerBalance,
  CreateAdjustmentJournalRequest,
  PostAdjustmentJournalRequest,
  ReverseAdjustmentJournalRequest,
  UpdateAdjustmentJournalRequest,
} from '../../types/fsa/adjustmentJournal.types';

class FsaAdjustmentJournalService {
  async getById(id: ID): Promise<ApiResponse<AdjustmentJournal>> {
    const r = await get<ApiResponse<AdjustmentJournal>>(fsaAdjustmentJournalEndpoints.get(id));
    return r.data;
  }

  async listByEngagement(
    engagementId: ID,
    query?: AdjustmentJournalQueryParameters,
  ): Promise<PaginatedResponse<AdjustmentJournal>> {
    const response = await get<any>(fsaAdjustmentJournalEndpoints.listByEngagement(engagementId, query));
    const raw = response.data;
    const pagedResult = raw.data ?? {};
    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getAuditedBalances(engagementId: ID): Promise<ApiResponse<AuditedLedgerBalance[]>> {
    const r = await get<ApiResponse<AuditedLedgerBalance[]>>(
      fsaAdjustmentJournalEndpoints.auditedBalances(engagementId),
    );
    return r.data;
  }

  async create(request: CreateAdjustmentJournalRequest): Promise<ApiResponse<AdjustmentJournal>> {
    const r = await post<ApiResponse<AdjustmentJournal>>(fsaAdjustmentJournalEndpoints.create(), request);
    return r.data;
  }

  async update(id: ID, request: UpdateAdjustmentJournalRequest): Promise<ApiResponse<AdjustmentJournal>> {
    const r = await put<ApiResponse<AdjustmentJournal>>(fsaAdjustmentJournalEndpoints.update(id), request);
    return r.data;
  }

  async remove(id: ID, rowVersion: number): Promise<ApiResponse<unknown>> {
    const r = await del<ApiResponse<unknown>>(fsaAdjustmentJournalEndpoints.remove(id, rowVersion));
    return r.data;
  }

  async post(id: ID, request: PostAdjustmentJournalRequest = {}): Promise<ApiResponse<AdjustmentJournal>> {
    const r = await post<ApiResponse<AdjustmentJournal>>(fsaAdjustmentJournalEndpoints.post(id), request);
    return r.data;
  }

  async reverse(id: ID, request: ReverseAdjustmentJournalRequest): Promise<ApiResponse<AdjustmentJournal>> {
    const r = await post<ApiResponse<AdjustmentJournal>>(fsaAdjustmentJournalEndpoints.reverse(id), request);
    return r.data;
  }

  /**
   * Engagement-wide adjustment-journal PDF bundle. Two render modes:
   *   • 'working-papers' — full audit detail (all statuses).
   *   • 'client-schedule' — POSTED only, simplified per-line view.
   *
   * Returns a Blob (Content-Type: application/pdf) the modal renders
   * inside an <iframe> via URL.createObjectURL — same blob-iframe path
   * used by the FSA template preview PDF.
   */
  async getBundlePdfBlob(
    engagementId: ID,
    mode: 'working-papers' | 'client-schedule',
    /** ADR-0026 — false leaves opening balance adjustments out of the bundle. */
    includeOpening = true,
  ): Promise<Blob> {
    const response = await get<Blob>(
      fsaAdjustmentJournalEndpoints.bundlePdf(engagementId, mode, includeOpening),
      { responseType: 'blob' },
    );
    return response.data;
  }

  /**
   * Engagement-wide adjustment-journal Excel bundle — Journals summary
   * sheet + flat Lines detail sheet. Same mode scope as the PDF bundle.
   * Returns a Blob (xlsx) the caller downloads via URL.createObjectURL.
   */
  async getBundleExcelBlob(
    engagementId: ID,
    mode: 'working-papers' | 'client-schedule',
    includeOpening = true,
  ): Promise<Blob> {
    const response = await get<Blob>(
      fsaAdjustmentJournalEndpoints.bundleExcel(engagementId, mode, includeOpening),
      { responseType: 'blob' },
    );
    return response.data;
  }
}

export const fsaAdjustmentJournalService = new FsaAdjustmentJournalService();
export default fsaAdjustmentJournalService;
